import { Component, Inject, OnInit } from "@angular/core";
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { FuseTranslationLoaderService } from "@fuse/services/translation-loader.service";
import { NotificationChannelModel, NotificationChannelModelType } from "app/models/notificationChannel.model";
import { locale as catalan } from "../../i18n/ca";
import { locale as english } from "../../i18n/en";
import { locale as spanish } from "../../i18n/es";

enum NotificationChannelAction {
    NEW = "NEW",
    EDIT = "EDIT",
}

@Component({
    selector: "app-project-notification-channel-add-edit",
    templateUrl: "./project-notification-channel-add-edit.component.html",
    styleUrls: ["./project-notification-channel-add-edit.component.scss"],
    standalone: false,
})
export class ProjectNotificationChannelAddEditComponent implements OnInit {
    readonly notificationChannelTypes = Object.values(NotificationChannelModelType);
    readonly languageOptions = [
        { id: "en", label: "English" },
        { id: "es", label: "Español" },
        { id: "ca", label: "Català" },
    ];
    readonly action: NotificationChannelAction;

    public channelForm: UntypedFormGroup;

    constructor(
        private formBuilder: UntypedFormBuilder,
        private translation: FuseTranslationLoaderService,
        public matDialogRef: MatDialogRef<ProjectNotificationChannelAddEditComponent>,
        @Inject(MAT_DIALOG_DATA) private data: { channel?: NotificationChannelModel },
    ) {
        this.translation.loadTranslations(english, spanish, catalan);
        this.action = data?.channel ? NotificationChannelAction.EDIT : NotificationChannelAction.NEW;
    }

    ngOnInit() {
        this.createForm();
        this.updateTypeValidators();
        this.channelForm.get("type")?.valueChanges.subscribe(() => this.updateTypeValidators());
    }

    isChannelType(type: NotificationChannelModelType | string): boolean {
        return this.channelForm.get("type")?.value === type;
    }

    hasStoredSecret(field: "hasWebhookUrl"): boolean {
        return !!this.channelForm.get(field)?.value;
    }

    getChannelTypeLabel(type: NotificationChannelModelType): string {
        return this.translation.translateService.instant(`PROJECTS.NotificationChannelType.${type}`);
    }

    save() {
        if (this.channelForm.invalid) {
            this.channelForm.markAllAsTouched();
            return;
        }

        this.matDialogRef.close({ channel: this.buildChannelPayload() });
    }

    cancel() {
        this.matDialogRef.close();
    }

    private createForm() {
        const channel = this.data?.channel ?? new NotificationChannelModel();
        const config = channel.config ?? {};

        this.channelForm = this.formBuilder.group({
            id: [channel.id],
            name: [channel.name ?? "", [Validators.required]],
            type: [channel.type ?? NotificationChannelModelType.Email, [Validators.required]],
            emailsInput: [(config.emails ?? []).join(", ")],
            phoneNumbersInput: [(config.phoneNumbers ?? []).join(", ")],
            webhookUrl: [config.webhookUrl ?? ""],
            hasWebhookUrl: [config.hasWebhookUrl ?? false],
            chatIdsInput: [(config.chatIds ?? []).join(", ")],
            language: [config.language ?? "en"],
        });
    }

    private updateTypeValidators() {
        const type = this.channelForm.get("type")?.value as NotificationChannelModelType | undefined;
        const emailsInput = this.channelForm.get("emailsInput");
        const phoneNumbersInput = this.channelForm.get("phoneNumbersInput");
        const webhookUrl = this.channelForm.get("webhookUrl");
        const chatIdsInput = this.channelForm.get("chatIdsInput");

        emailsInput?.clearValidators();
        phoneNumbersInput?.clearValidators();
        webhookUrl?.clearValidators();
        chatIdsInput?.clearValidators();

        if (type === NotificationChannelModelType.Email) {
            emailsInput?.setValidators([this.listRequiredValidator()]);
        }

        if (type === NotificationChannelModelType.SMS) {
            phoneNumbersInput?.setValidators([this.listRequiredValidator()]);
        }

        if (type === NotificationChannelModelType.Slack || type === NotificationChannelModelType.Teams) {
            webhookUrl?.setValidators([this.secretAwareRequiredValidator("hasWebhookUrl")]);
        }

        if (type === NotificationChannelModelType.Telegram) {
            chatIdsInput?.setValidators([this.listRequiredValidator()]);
        }

        emailsInput?.updateValueAndValidity({ emitEvent: false });
        phoneNumbersInput?.updateValueAndValidity({ emitEvent: false });
        webhookUrl?.updateValueAndValidity({ emitEvent: false });
        chatIdsInput?.updateValueAndValidity({ emitEvent: false });
    }

    private listRequiredValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null =>
            this.parseList(control.value).length > 0 ? null : { listRequired: true };
    }

    private secretAwareRequiredValidator(storedFlagField: "hasWebhookUrl"): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const stored = !!this.channelForm?.get(storedFlagField)?.value;
            const value = `${control.value ?? ""}`.trim();
            return stored || value ? null : { required: true };
        };
    }

    private buildChannelPayload(): NotificationChannelModel {
        const value = this.channelForm.getRawValue();

        return {
            id: value.id,
            name: value.name?.trim(),
            type: value.type,
            enabled: this.data?.channel?.enabled !== false,
            config: {
                emails: this.parseList(value.emailsInput),
                phoneNumbers: this.parseList(value.phoneNumbersInput),
                webhookUrl: value.webhookUrl?.trim() || undefined,
                hasWebhookUrl: value.hasWebhookUrl ?? false,
                chatIds: this.parseList(value.chatIdsInput),
                language: value.language?.trim() || undefined,
            },
        };
    }

    private parseList(value?: string): string[] {
        return (value ?? "")
            .split(/[\n,]/)
            .map((item) => item.trim())
            .filter((item) => item.length > 0);
    }
}
